import {
  Conditions,
  Operators,
  Properties,
  QualifierExplanation,
} from "@/prisma-package/src";

const PROPERTY_LABELS: Record<string, string> = {
  [Properties.SRC]: "Source",
  [Properties.TRANSACTION_NUMBER]: "Transaction number",
  [Properties.AMOUNT]: "Amount",
  [Properties.ACCOUNT_CODE]: "Account code",
  [Properties.ACCOUNT_DESCRIPTION]: "Account description",
  [Properties.DESCRIPTION]: "Description",
  [Properties.VENDOR_NAME]: "Vendor name",
  [Properties.LABOR_LINE]: "Labor line",
  [Properties.ABOVE_THE_LINE]: "Above the line",
  [Properties.TAX_INCENTIVE_STATUS]: "Tax incentive status",
  [Properties.DATE_OF_EXPENDITURE]: "Date of expenditure",
  [Properties.FF1]: "Free field 1",
  [Properties.FF2]: "Free field 2",
  [Properties.LEDGER_FF1]: "Ledger free field 1",
  [Properties.LEDGER_FF2]: "Ledger free field 2",
  [Properties.LOCATION]: "Location",
  [Properties.EMPLOYEE_LAST_NAME]: "Employee last name",
  [Properties.EMPLOYEE_JOB_TITLE]: "Employee job title",
  [Properties.EMPLOYEE_STATE_OF_RESIDENCY]: "Employee state of residency",
  [Properties.EMPLOYEE_ETHNICITY]: "Employee ethnicity",
  [Properties.EMPLOYEE_GENDER]: "Employee gender",
  [Properties.EMPLOYEE_CORP_NAME]: "Employee corp name",
  [Properties.EMPLOYEE_LAST_FOUR_SSN]: "Employee last four SSN",
  [Properties.EMPLOYEE_LAST_FOUR_EIN]: "Employee last four EIN",
  [Properties.EMPLOYEE_TYPE]: "Employee type",
  [Properties.EMPLOYEE_ZIP]: "Employee zip code",
  [Properties.VENDOR_ADDRESS_LINE_ONE]: "Vendor address line one",
  [Properties.VENDOR_ADDRESS_LINE_TWO]: "Vendor address line two",
  [Properties.VENDOR_CITY]: "Vendor city",
  [Properties.VENDOR_STATE]: "Vendor state",
  [Properties.VENDOR_ZIP]: "Vendor zip code",
  [Properties.VENDOR_PHONE]: "Vendor phone",
  [Properties.VENDOR_EMAIL]: "Vendor email",
  [Properties.VENDOR_WEBSITE]: "Vendor website",
  [Properties.VENDOR_HAS_REQUIRED_DOCS]: "Vendor has required documents",
};

const OPERATOR_LABELS: Record<string, string> = {
  [Operators.INCLUDES]: "contains",
  [Operators.EQUALS]: "is",
  [Operators.IS_BEFORE]: "is before",
  [Operators.IS_AFTER]: "is after",
};

const _titleCase = (value: string): string =>
  value
    .replace(/[_-]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/^./, (char) => char.toUpperCase());

export const humanizeProperty = (propertyName: string): string => {
  const label = PROPERTY_LABELS[propertyName];
  if (label) {
    return label;
  }
  const lastSegment = propertyName.split(".").pop() ?? propertyName;
  return _titleCase(lastSegment);
};

// One plain-English sentence for a qualifier condition, e.g.
// `Account description contains "camera" or "lens"`. Nested children are
// rendered by the UI as their own indented rows, not inlined here.
export const humanizeQualifier = (
  qualifier: Pick<
    QualifierExplanation,
    "propertyName" | "operator" | "modifier" | "values"
  >
): string => {
  if (qualifier.propertyName === Properties.VENDOR_HAS_REQUIRED_DOCS) {
    return "Vendor has all required documents on file";
  }

  const property = humanizeProperty(qualifier.propertyName);
  const operator =
    OPERATOR_LABELS[qualifier.operator] ?? qualifier.operator;
  const joiner =
    qualifier.modifier === Conditions.AND ? '" and "' : '" or "';
  const values = `"${qualifier.values.join(joiner)}"`;

  return `${property} ${operator} ${values}`;
};

export const humanizeActualValue = (
  actualValue: string | null
): string => {
  if (actualValue === null || actualValue === "") {
    return "Not available";
  }
  if (actualValue === "true") {
    return "Yes";
  }
  if (actualValue === "false") {
    return "No";
  }
  return actualValue;
};

export const humanizeDocType = (
  docType: string,
  docTypeLabels?: Record<string, string>
): string => docTypeLabels?.[docType] ?? _titleCase(docType);
